import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from '../api';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'customer' | 'merchant' | 'admin' | 'delivery';
  status: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<any>;
  loginWith2FA: (userId: string, otp: string) => Promise<void>;
  register: (name: string, email: string, phone: string, password: string, role?: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('bitehub_token');
      const storedUser = localStorage.getItem('bitehub_user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        // Verify token by fetching current user
        try {
          const response = await axios.get('/api/auth/me');
          if (response.data.success) {
            setUser(response.data.user);
            localStorage.setItem('bitehub_user', JSON.stringify(response.data.user));
          }
        } catch (error) {
          // Token invalid, clear auth
          console.error('Token validation failed:', error);
          logout();
        }
      }

      setIsLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });

      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requires2FA) {
          return {
            requires2FA: true,
            userId: response.data.userId,
            email: response.data.email
          };
        }

        // Normal login flow
        const { token: newToken, user: newUser } = response.data;

        setToken(newToken);
        setUser(newUser);

        // Store in localStorage
        localStorage.setItem('bitehub_token', newToken);
        localStorage.setItem('bitehub_user', JSON.stringify(newUser));

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        return { requires2FA: false };
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const loginWith2FA = async (userId: string, token: string) => {
    try {
      const response = await axios.post('/api/auth/verify-2fa', { userId, token });

      if (response.data.success) {
        const { token: newToken, user: newUser } = response.data;

        setToken(newToken);
        setUser(newUser);

        // Store in localStorage
        localStorage.setItem('bitehub_token', newToken);
        localStorage.setItem('bitehub_user', JSON.stringify(newUser));

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } else {
        throw new Error(response.data.error || '2FA verification failed');
      }
    } catch (error: any) {
      console.error('2FA login error:', error);
      throw new Error(error.response?.data?.error || error.message || '2FA verification failed');
    }
  };

  const register = async (name: string, email: string, phone: string, password: string, role: string = 'customer') => {
    try {
      const response = await axios.post('/api/auth/register', {
        name,
        email,
        phone,
        password,
        role
      });

      if (response.data.success) {
        const { token: newToken, user: newUser } = response.data;

        setToken(newToken);
        setUser(newUser);

        // Store in localStorage
        localStorage.setItem('bitehub_token', newToken);
        localStorage.setItem('bitehub_user', JSON.stringify(newUser));

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Register error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);

    // Clear localStorage
    localStorage.removeItem('bitehub_token');
    localStorage.removeItem('bitehub_user');

    // Remove axios default header
    delete axios.defaults.headers.common['Authorization'];

    // Redirect to login
    window.location.href = '/login';
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('bitehub_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    loginWith2FA,
    register,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
